import { useState } from 'react';
import type { FormEvent } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { JobEntry, JobStatus, PlatformAdmin, page } from '@cloudsign/shared';
import { z } from 'zod';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { useCan, useCaps } from '../../lib/session';
import { useListState } from '../../lib/useList';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { Badge, Button, Card, ConfirmButton, EmptyRow, FilterBar, PageHeader, Pager, SelectField, Spinner, TableWrap, TextField, Th } from '../../components/ui';

const AdminList = z.array(PlatformAdmin);
const JobPage = page(JobEntry);

export function AdminsPage() {
  const qc = useQueryClient();
  const caps = useCaps();
  const q = useQuery({ queryKey: ['platform', 'admins'], queryFn: () => api.get('/platform/admins', { schema: AdminList }) });
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const refresh = () => qc.invalidateQueries({ queryKey: ['platform', 'admins'] });
  const add = useMutation({
    mutationFn: () => api.post('/platform/admins', { body: { email, fullName }, schema: PlatformAdmin }),
    onSuccess: async () => {
      setEmail('');
      setFullName('');
      await refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (accountId: string) => api.delete(`/platform/admins/${accountId}`),
    onSuccess: refresh,
  });
  const errs = fieldErrors(add.error);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    add.mutate();
  };
  return (
    <>
      <PageHeader title={t('nav.platform.admins')} description={t('nav.platform.admins.desc')} />
      <div className="space-y-6">
        <Card title={t('admins.add')}>
          <form onSubmit={submit} className="grid gap-4 sm:grid-cols-3">
            <div className="sm:col-span-3">
              <ErrorLine error={add.error} />
            </div>
            <TextField label={t('auth.email')} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} error={errs['email']} />
            <TextField label={t('auth.fullName')} required value={fullName} onChange={(e) => setFullName(e.target.value)} error={errs['fullName']} />
            <div className="flex items-end">
              <Button type="submit" variant="primary" disabled={add.isPending}>
                {t('admins.add')}
              </Button>
            </div>
          </form>
        </Card>
        {q.isPending ? (
          <Spinner />
        ) : q.isError ? (
          <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
        ) : (
          <>
            <ErrorLine error={remove.error} />
            <TableWrap caption={t('nav.platform.admins')}>
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  <Th>{t('common.name')}</Th>
                  <Th>{t('auth.email')}</Th>
                  <Th>{t('tenants.created')}</Th>
                  <Th>{t('common.actions')}</Th>
                </tr>
              </thead>
              <tbody>
                {q.data.length === 0 ? <EmptyRow cols={4} /> : null}
                {q.data.map((a) => (
                  <tr key={a.accountId} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3">{a.fullName}</td>
                    <td className="px-4 py-3">{a.email}</td>
                    <td className="px-4 py-3">{formatDateTime(a.createdAt)}</td>
                    <td className="px-4 py-3">
                      {a.accountId === caps.account.id ? (
                        <Badge>{t('admins.you')}</Badge>
                      ) : (
                        <ConfirmButton label={t('admins.remove')} pending={remove.isPending} onConfirm={() => remove.mutate(a.accountId)} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          </>
        )}
      </div>
    </>
  );
}

export function JobsPage() {
  const qc = useQueryClient();
  const canRetry = useCan('platform.jobs_manage');
  const list = useListState();
  const [status, setStatus] = useState('');
  const q = useQuery({
    queryKey: ['platform', 'jobs', { page: list.page, search: list.search, status }],
    queryFn: () => api.get('/platform/jobs', { query: { page: list.page, pageSize: 25, name: list.search, status }, schema: JobPage }),
    placeholderData: keepPreviousData,
  });
  const retry = useMutation({
    mutationFn: (id: string) => api.post(`/platform/jobs/${id}/retry`, { schema: JobEntry }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'jobs'] }),
  });
  return (
    <>
      <PageHeader title={t('nav.platform.jobs')} description={t('nav.platform.jobs.desc')} />
      <FilterBar columns="sm:grid-cols-2">
        <TextField label={t('jobs.name')} type="search" value={list.search} onChange={(e) => list.setSearch(e.target.value)} />
        <SelectField
          label={t('common.status')}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            list.resetPage();
          }}
        >
          <option value="">{t('common.all')}</option>
          {JobStatus.options.map((s) => (
            <option key={s} value={s}>
              {t(`status.${s}`)}
            </option>
          ))}
        </SelectField>
      </FilterBar>
      <ErrorLine error={retry.error} />
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <>
          <TableWrap caption={t('nav.platform.jobs')}>
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <Th>{t('jobs.name')}</Th>
                <Th>{t('common.status')}</Th>
                <Th>{t('jobs.attempts')}</Th>
                <Th>{t('jobs.runAt')}</Th>
                <Th>{t('emails.error')}</Th>
                <Th>{t('common.actions')}</Th>
              </tr>
            </thead>
            <tbody>
              {q.data.items.length === 0 ? <EmptyRow cols={6} /> : null}
              {q.data.items.map((j) => (
                <tr key={j.id} className="border-t border-slate-100 hover:bg-slate-50 align-top">
                  <td className="px-4 py-3 font-mono">{j.name}</td>
                  <td className="px-4 py-3">
                    <Badge tone={j.status === 'done' ? 'good' : j.status === 'failed' ? 'bad' : 'warn'}>{t(`status.${j.status}`)}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    {j.attempts} / {j.maxAttempts}
                  </td>
                  <td className="px-4 py-3">{formatDateTime(j.runAt)}</td>
                  <td className="px-4 py-3">{j.lastError ?? ''}</td>
                  <td className="px-4 py-3">
                    {canRetry && j.status === 'failed' ? (
                      <Button disabled={retry.isPending} onClick={() => retry.mutate(j.id)}>
                        {t('jobs.retry')}
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
          <Pager page={q.data.page} pageSize={q.data.pageSize} total={q.data.total} onPage={list.setPage} />
        </>
      )}
    </>
  );
}
